import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * State, plus a ref that is already current before the next render.
 *
 * The studio's headless pass sets several things and then captures in the
 * same run: the lens, the grid's bearing, the aspect ratio. Everything it
 * saved afterwards read those from state — and state does not change until
 * React renders again, so the session recorded the values the pass had just
 * replaced. The capture was right and what got written down was one step
 * behind it, which is a horrible bug to look at: the picture proves the code
 * worked.
 *
 * Rendering keeps using the state. Anything running inside the same pass
 * reads `ref.current`, which is written at the same moment as the state.
 *
 * A hook rather than three pairs of variables, because this was fixed once
 * for the lens and the same bug was still sitting in the other two.
 */
export function useLiveState<T>(initial: T): [T, (next: T) => void, React.MutableRefObject<T>] {
    const [value, setValue] = useState<T>(initial);
    const ref = useRef<T>(initial);

    // Keeps the ref honest when the state changes by any other route.
    useEffect(() => { ref.current = value; }, [value]);

    const set = useCallback((next: T) => {
        ref.current = next;
        setValue(next);
    }, []);

    return [value, set, ref];
}
